import React, { useState } from "react";
import { FaNewspaper, FaThumbtack, FaChevronDown, FaChevronUp } from "react-icons/fa";

/**
 * Pinned news article card for the top of the feed.
 * Shows headline + subtitle collapsed; expands to full article on click.
 * Styled to look like a shared news link on a social platform.
 * Non-interactive (no like/comment/share buttons).
 */
const PinnedArticleCard = ({ article }) => {
  const [expanded, setExpanded] = useState(false);

  if (!article) return null;

  const {
    publication = "The Global Tribune",
    headline = "",
    subtitle = "",
    author = "",
    authorTitle = "",
    date = "",
    paragraphs = [],
    pullQuote = null,
    pullQuoteAfter = 2,
    figures = [],
    figuresAfter = null,
  } = article;

  // Renders figure exhibit(s) as simple horizontal bar charts (matches NewsArticlePage).
  const renderFigures = (keyPrefix) => (
    <div key={keyPrefix} className="my-4 space-y-4 not-italic">
      {figures.map((fig, fi) => {
        const bars = fig.bars || [];
        const max = Math.max(1, ...bars.map((b) => Number(b.value) || 0));
        return (
          <figure
            key={`${keyPrefix}-${fig.id || fi}`}
            className="border border-stone-300 bg-stone-100 rounded p-4"
          >
            <figcaption className="mb-3">
              {fig.label && (
                <div className="text-[10px] font-sans font-semibold tracking-[0.15em] uppercase text-stone-500">
                  {fig.label}
                </div>
              )}
              {fig.title && (
                <div className="font-serif text-sm font-bold text-stone-900 leading-snug">
                  {fig.title}
                </div>
              )}
            </figcaption>
            <div>
              {bars.map((bar, bi) => {
                const pct = Math.round(((Number(bar.value) || 0) / max) * 100);
                const display = bar.display != null ? bar.display : bar.value;
                return (
                  <div key={bi} className="mb-2.5 last:mb-0">
                    <div className="flex items-baseline justify-between gap-3 text-xs font-sans text-stone-700 mb-1">
                      <span className="leading-snug">{bar.label}</span>
                      <span className="font-semibold tabular-nums text-stone-900 flex-shrink-0">
                        {display}
                      </span>
                    </div>
                    <div
                      className="h-3 w-full bg-stone-200 rounded-sm overflow-hidden"
                      role="img"
                      aria-label={`${bar.label}: ${display}`}
                    >
                      <div
                        className={`h-full rounded-sm ${bar.highlight ? "bg-stone-900" : "bg-stone-600"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {fig.credit && (
              <div className="mt-3 text-[10px] text-stone-400 font-sans">
                {fig.credit}
              </div>
            )}
          </figure>
        );
      })}
    </div>
  );

  return (
    <div className="bg-spiders-bg-surface rounded-2xl overflow-hidden">
      {/* Pinned label */}
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-1">
        <FaThumbtack className="w-3 h-3 text-spiders-text-muted" />
        <span className="text-xs text-spiders-text-muted font-medium">Pinned — Today's Topic</span>
      </div>

      {/* News source header */}
      <div className="flex items-center gap-2.5 px-4 py-2">
        <div className="w-9 h-9 rounded-full bg-spiders-bg-elevated flex items-center justify-center">
          <FaNewspaper className="w-4 h-4 text-spiders-text-muted" />
        </div>
        <div>
          <div className="text-sm font-semibold text-spiders-text-primary">{publication}</div>
          <div className="text-xs text-spiders-text-muted">{date}</div>
        </div>
      </div>

      {/* Collapsed preview — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 pb-3 group"
      >
        <h3 className="text-[15px] font-bold text-spiders-text-primary leading-snug mb-1 group-hover:text-spiders-primary-400 transition-colors">
          {headline}
        </h3>
        {subtitle && (
          <p className="text-sm text-spiders-text-secondary leading-snug mb-2">
            {subtitle}
          </p>
        )}
        <div className="flex items-center gap-1 text-xs text-spiders-primary-400">
          {expanded ? (
            <>
              <FaChevronUp className="w-2.5 h-2.5" />
              <span>Hide full article</span>
            </>
          ) : (
            <>
              <FaChevronDown className="w-2.5 h-2.5" />
              <span>Read full article</span>
            </>
          )}
        </div>
      </button>

      {/* Expanded article content */}
      {expanded && (
        <div className="border-t border-spiders-border">
          <div className="bg-stone-50 mx-3 mb-3 mt-3 rounded-lg overflow-hidden border border-stone-200">
            {/* Byline */}
            <div className="px-5 pt-4 pb-3 border-b border-stone-200">
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <span>
                  By <span className="font-semibold text-stone-700">{author}</span>
                </span>
                {authorTitle && (
                  <>
                    <span className="text-stone-300">|</span>
                    <span>{authorTitle}</span>
                  </>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4 font-serif text-[15px] text-stone-800 leading-relaxed space-y-3">
              {paragraphs.map((para, i) => {
                const blocks = [
                  <p
                    key={`p-${i}`}
                    className={
                      i === 0
                        ? "first-letter:text-4xl first-letter:font-bold first-letter:float-left first-letter:mr-1 first-letter:mt-0.5 first-letter:leading-none first-letter:text-stone-900"
                        : ""
                    }
                  >
                    {para}
                  </p>,
                ];
                if (pullQuote && i + 1 === pullQuoteAfter) {
                  blocks.push(
                    <blockquote
                      key={`pq-${i}`}
                      className="my-4 mx-2 sm:mx-6 py-3 px-4 border-l-4 border-stone-800 bg-stone-100 rounded-r"
                    >
                      <p className="font-serif text-base italic text-stone-700 leading-snug">
                        {pullQuote.text}
                      </p>
                      {pullQuote.attribution && (
                        <footer className="mt-2 text-xs text-stone-500 font-sans">
                          — {pullQuote.attribution}
                        </footer>
                      )}
                    </blockquote>
                  );
                }
                if (figures.length > 0 && figuresAfter !== null && i + 1 === figuresAfter) {
                  blocks.push(renderFigures(`fig-${i}`));
                }
                return blocks;
              })}

              {figures.length > 0 &&
                (figuresAfter === null || figuresAfter > paragraphs.length) &&
                renderFigures("fig-end")}
            </div>

            {/* Footer rule */}
            <div className="mx-5 mb-3">
              <div className="h-px bg-stone-300" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PinnedArticleCard;
